/* eslint-disable react-refresh/only-export-components */
/**
 * ThemeContext - Refactored with Split Contexts
 *
 * Splits theme context into separate value and action contexts to prevent
 * unnecessary re-renders when theme is updated.
 *
 * Key improvements:
 * - Separate contexts for values (theme, mode, loading) and actions (setTheme, toggleMode)
 * - Components only re-render if they use the values that changed
 * - Actions are stable and don't cause re-renders
 * - Better performance for components that only need to set theme, not read it
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { themesApi, type Theme } from '../services/themeApi'
import {
  settingsApi,
  getSettingsCached,
  BRANDING_SETTINGS_CHANGED_EVENT,
  type BrandingSettingsChangedDetail,
} from '../services/settingsApi'
import { logger } from '../utils/logger'
import { useAuth } from './AuthContext'
import { config } from '../config'

type ThemeMode = 'light' | 'dark'

// Separate value and action contexts
interface ThemeValueContextType {
  theme: Theme | null
  mode: ThemeMode
  loading: boolean
  saving: boolean
  canOverrideTheme: boolean
  lockedBy: 'none' | 'platform' | 'tenant'
}

interface ThemeActionContextType {
  setTheme: (theme: Theme) => void
  toggleMode: () => void
  setMode: (mode: ThemeMode) => void
  applyTheme: (theme: Theme, mode: ThemeMode) => void
}

const ThemeValueContext = createContext<ThemeValueContextType | undefined>(undefined)
const ThemeActionContext = createContext<ThemeActionContextType | undefined>(undefined)

// Storage keys from centralized config
const THEME_STORAGE_KEY = config.storage.theme
const MODE_STORAGE_KEY = config.storage.themeMode

/** Inline vars ShadnPro sets and other themes do not. Cleared so they cannot leak. */
const OPTIONAL_INLINE_THEME_KEYS = [
  'primary-foreground',
  'stroke-strong',
  'border-color',
  'unified-bg',
  'nav-bg',
  'nav-hover',
  'nav-active',
  'nav-border',
  'topbar-bg',
  'topbar-border',
  'workspace-bg',
  'card-bg',
  'card-border',
  'input-bg',
  'focus-shadow',
  'primary-alpha-10',
  'primary-alpha-15',
  'primary-alpha-20',
  'selection-bg',
  'selection-border',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
] as const

function clearOptionalThemeVars(root: HTMLElement, colors: Record<string, string>) {
  for (const key of OPTIONAL_INLINE_THEME_KEYS) {
    if (!colors[key]) root.style.removeProperty(`--${key}`)
  }
}

/** Read mode from localStorage (or pre-paint data-theme). Safe for SSR / private mode. */
function getInitialThemeMode(): ThemeMode {
  if (typeof document !== 'undefined') {
    const fromDom = document.documentElement.dataset.theme
    if (fromDom === 'dark' || fromDom === 'light') return fromDom
  }
  try {
    const saved = localStorage.getItem(MODE_STORAGE_KEY)
    if (saved === 'dark' || saved === 'light') return saved
  } catch {
    // ignore
  }
  return 'light'
}

/** Apply mode attrs immediately so CSS dark tokens win before theme API returns. */
function applyModeToDocument(selectedMode: ThemeMode) {
  const root = document.documentElement
  root.dataset.theme = selectedMode
  root.classList.toggle('dark-mode', selectedMode === 'dark')
  root.style.colorScheme = selectedMode
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme | null>(null)
  const [mode, setModeState] = useState<ThemeMode>(getInitialThemeMode)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [canOverrideTheme, setCanOverrideTheme] = useState(true)
  const [lockedBy, setLockedBy] = useState<'none' | 'platform' | 'tenant'>('none')
  const { isAuthenticated, isCheckingAuth } = useAuth()

  // Apply theme colors to CSS variables
  // Memoized to ensure stable reference
  const applyTheme = useCallback((selectedTheme: Theme, selectedMode: ThemeMode) => {
    const root = document.documentElement
    const colors = selectedMode === 'light' ? selectedTheme.light_colors : selectedTheme.dark_colors

    // Set data-theme attribute for CSS (this applies base light/dark styles)
    applyModeToDocument(selectedMode)
    root.dataset.colorTheme = selectedTheme.id

    clearOptionalThemeVars(root, colors)

    // Apply theme color variables (only override the themed colors, not structural ones)
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value)
    })

    // Also apply base structural colors based on mode if not in theme
    if (selectedMode === 'dark') {
      // Ensure dark mode structural colors are set if not overridden
      if (!colors['bg']) root.style.setProperty('--bg', '#0d1117')
      if (!colors['surface']) root.style.setProperty('--surface', '#161b22')
      if (!colors['surface-alt']) root.style.setProperty('--surface-alt', '#1c2128')
      if (!colors['text']) root.style.setProperty('--text', '#f0f6fc')
      if (!colors['text-muted']) root.style.setProperty('--text-muted', '#8b949e')
      if (!colors['stroke']) root.style.setProperty('--stroke', '#30363d')
      if (!colors['stroke-light']) root.style.setProperty('--stroke-light', '#21262d')
    } else {
      // Ensure light mode structural colors are set if not overridden
      if (!colors['bg']) root.style.setProperty('--bg', '#ffffff')
      if (!colors['surface']) root.style.setProperty('--surface', '#f8f9fa')
      if (!colors['surface-alt']) root.style.setProperty('--surface-alt', '#f1f3f5')
      if (!colors['text']) root.style.setProperty('--text', '#1a1a1a')
      if (!colors['text-muted']) root.style.setProperty('--text-muted', '#6c757d')
      if (!colors['stroke']) root.style.setProperty('--stroke', '#dee2e6')
      if (!colors['stroke-light']) root.style.setProperty('--stroke-light', '#e9ecef')
    }
  }, [])

  // Load saved theme or default
  // Optimized: Load theme immediately without waiting for auth (themes are public endpoints)
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // Sync mode to DOM immediately (before any await) so dark CSS applies
        // even while themesApi is in flight. Pre-paint script in index.html
        // already set this for first paint; this covers React remounts / HMR.
        const initialMode = getInitialThemeMode()
        setModeState(initialMode)
        applyModeToDocument(initialMode)

        // Load saved theme ID
        const savedThemeId = localStorage.getItem(THEME_STORAGE_KEY)

        // Apply default theme immediately for instant UI (non-blocking)
        // This prevents the shimmer from showing too long
        let defaultTheme: Theme
        try {
          defaultTheme = await themesApi.getDefault()
          // Apply default theme immediately so user sees styled UI
          setThemeState(defaultTheme)
          applyTheme(defaultTheme, initialMode)
          setLoading(false) // Mark as loaded so app can render
        } catch (error) {
          logger.error('Failed to load default theme', error)
          setLoading(false) // Still allow app to render even if theme fails
          return
        }

        // Then load saved theme in background (if different from default)
        if (savedThemeId && savedThemeId !== defaultTheme.id) {
          try {
            const loadedTheme = await themesApi.get(savedThemeId)
            setThemeState(loadedTheme)
            applyTheme(loadedTheme, initialMode)
          } catch {
            // If saved theme not found, keep default theme (already applied)
            logger.warn('Saved theme not found, using default')
          }
        }
      } catch (error) {
        logger.error('Failed to load theme', error)
        setLoading(false) // Always allow app to render
      }
    }

    loadTheme()
  }, [applyTheme]) // applyTheme is now stable due to useCallback

  // After login, sync effective theme from settings (respects admin hierarchy)
  useEffect(() => {
    if (!isAuthenticated || isCheckingAuth || loading) return

    const syncDbTheme = async () => {
      try {
        const settings = await getSettingsCached()
        const themeName = settings.effective_theme_name || settings.theme_name
        if (!themeName) return

        const savedMode = (settings.effective_theme_mode || settings.theme_mode || 'light') as ThemeMode
        const dbTheme = await themesApi.get(themeName)
        setThemeState(dbTheme)
        setModeState(savedMode)
        applyTheme(dbTheme, savedMode)
        setCanOverrideTheme(settings.can_override_theme !== false)
        setLockedBy(settings.locked_by || 'none')

        if (settings.can_override_theme !== false) {
          localStorage.setItem(THEME_STORAGE_KEY, dbTheme.id)
          localStorage.setItem(MODE_STORAGE_KEY, savedMode)
        } else {
          localStorage.removeItem(THEME_STORAGE_KEY)
          localStorage.removeItem(MODE_STORAGE_KEY)
        }
      } catch (error) {
        logger.debug('Failed to sync theme from DB, keeping current theme', error)
      }
    }

    syncDbTheme()
  }, [isAuthenticated, isCheckingAuth, loading, applyTheme])

  // Re-sync when platform/tenant admin updates branding policy
  useEffect(() => {
    if (!isAuthenticated || isCheckingAuth) return

    const syncDbTheme = async (preview?: BrandingSettingsChangedDetail) => {
      try {
        const settings = await getSettingsCached()
        const previewThemeName =
          preview?.previewThemeName ?? settings.effective_theme_name ?? settings.theme_name
        if (!previewThemeName) return

        const savedMode = (settings.effective_theme_mode || settings.theme_mode || 'light') as ThemeMode
        const previewMode = preview?.previewThemeMode ?? savedMode
        const dbTheme = await themesApi.get(previewThemeName)
        setThemeState(dbTheme)
        setModeState(previewMode)
        applyTheme(dbTheme, previewMode)
        setCanOverrideTheme(settings.can_override_theme !== false)
        setLockedBy(settings.locked_by || 'none')

        if (settings.can_override_theme !== false) {
          localStorage.setItem(THEME_STORAGE_KEY, dbTheme.id)
          localStorage.setItem(MODE_STORAGE_KEY, previewMode)
        } else {
          localStorage.removeItem(THEME_STORAGE_KEY)
          localStorage.removeItem(MODE_STORAGE_KEY)
        }
      } catch (error) {
        logger.debug('Failed to re-sync theme after branding change', error)
      }
    }

    const onBrandingChanged = (event: Event) => {
      const detail = (event as CustomEvent<BrandingSettingsChangedDetail | undefined>).detail
      void syncDbTheme(detail)
    }
    window.addEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingChanged)
    return () => window.removeEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingChanged)
  }, [isAuthenticated, isCheckingAuth, applyTheme])

  // Memoized action callbacks
  const setTheme = useCallback(
    (newTheme: Theme) => {
      if (!canOverrideTheme) {
        logger.warn('Theme is locked by administrator')
        return
      }
      setThemeState(newTheme)
      localStorage.setItem(THEME_STORAGE_KEY, newTheme.id)
      applyTheme(newTheme, mode)

      // Save to backend via Settings API with loading state
      setSaving(true)
      settingsApi
        .updateTheme(newTheme.name, mode)
        .catch((error) => {
          logger.error('Failed to save theme preference to backend', error)
          // Don't throw - allow local storage to work even if API fails
        })
        .finally(() => {
          setSaving(false)
        })
    },
    [mode, applyTheme, canOverrideTheme]
  )

  const setMode = useCallback(
    (newMode: ThemeMode) => {
      if (!canOverrideTheme) {
        logger.warn('Theme mode is locked by administrator')
        return
      }
      setModeState(newMode)
      localStorage.setItem(MODE_STORAGE_KEY, newMode)
      if (theme) {
        applyTheme(theme, newMode)

        // Save to backend via Settings API with loading state
        setSaving(true)
        settingsApi
          .updateTheme(theme.name, newMode)
          .catch((error) => {
            logger.error('Failed to save theme mode preference to backend', error)
            // Don't throw - allow local storage to work even if API fails
          })
          .finally(() => {
            setSaving(false)
          })
      }
    },
    [theme, applyTheme, canOverrideTheme]
  )

  const toggleMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light')
  }, [mode, setMode])

  // Memoize value objects to prevent unnecessary re-renders
  const valueContextValue = useMemo<ThemeValueContextType>(
    () => ({
      theme,
      mode,
      loading,
      saving,
      canOverrideTheme,
      lockedBy,
    }),
    [theme, mode, loading, saving, canOverrideTheme, lockedBy]
  )

  const actionContextValue = useMemo<ThemeActionContextType>(
    () => ({
      setTheme,
      toggleMode,
      setMode,
      applyTheme,
    }),
    [setTheme, toggleMode, setMode, applyTheme]
  )

  return (
    <ThemeValueContext.Provider value={valueContextValue}>
      <ThemeActionContext.Provider value={actionContextValue}>
        {children}
      </ThemeActionContext.Provider>
    </ThemeValueContext.Provider>
  )
}

/**
 * Hook to access theme values (theme, mode, loading, saving)
 * Components using this will re-render when these values change
 */
export const useThemeValue = () => {
  const context = useContext(ThemeValueContext)
  if (context === undefined) {
    throw new Error('useThemeValue must be used within a ThemeProvider')
  }
  return context
}

/**
 * Hook to access theme actions (setTheme, toggleMode, setMode, applyTheme)
 * Components using this will NOT re-render when theme values change
 */
export const useThemeActions = () => {
  const context = useContext(ThemeActionContext)
  if (context === undefined) {
    throw new Error('useThemeActions must be used within a ThemeProvider')
  }
  return context
}

/**
 * Combined hook for backward compatibility
 * Use useThemeValue and useThemeActions separately for better performance
 */
export const useTheme = () => {
  const value = useThemeValue()
  const actions = useThemeActions()
  return { ...value, ...actions }
}

/**
 * Selector hooks for specific values
 * Use these to only subscribe to specific values you need
 */
export const useThemeMode = () => {
  const { mode } = useThemeValue()
  return mode
}

export const useThemeLoading = () => {
  const { loading } = useThemeValue()
  return loading
}

export const useThemeSaving = () => {
  const { saving } = useThemeValue()
  return saving
}

export const useCurrentTheme = () => {
  const { theme } = useThemeValue()
  return theme
}
